import React, {useEffect, useMemo, useState} from 'react';
import {Button, Col, DatePicker, Divider, Form, InputNumber, Modal, Row, Select} from "antd";
import {useCollectionAssignPurchasePriceMutation, useGetTransactionVenuesQuery} from "@mwServices/User";
import {showResponse} from "@services/Api";
import {formatMoney, formatTitleCase} from "@lib/format";
import TextArea from "antd/es/input/TextArea";
import {useWatch} from "antd/es/form/Form";
import {Issue} from "@mwServices/Issue";
import moment from "moment";

interface AssignPurchasePriceModalProps {
    isModalOpen: boolean;
    onCancel: () => void;
    selectedComicIds?: number[];
    getItem: (id) => Issue;
}
const AssignPurchasePriceModal = ({isModalOpen, onCancel, selectedComicIds, getItem}: AssignPurchasePriceModalProps) => {
    const [form] = Form.useForm();
    const [assignIssuesPurchasePrice, assignIssuesPurchasePriceResponse] = useCollectionAssignPurchasePriceMutation();
    const venuesQuery = useGetTransactionVenuesQuery();
    const [venues, setVenues] = useState<{ venue: string }[]>([]);

    useEffect(() => {
        showResponse(assignIssuesPurchasePriceResponse, 'Purchase Price set.', 'Sorry, there was a problem setting the Purchase Price on the issues.');
        onCancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignIssuesPurchasePriceResponse]);

    useEffect(() => {
        if (venuesQuery.data) {
            setVenues(venuesQuery.data);
        }
    }, [venuesQuery.data]);

    const purchasePrice = useWatch('purchase_price', form);
    const shippingFees = useWatch('purchase_shipping_fees', form);
    const addFees = useWatch('purchase_additional_expenses', form);
    const totalComicCost = useMemo(() => {
        return [purchasePrice, shippingFees, addFees].reduce((newTotal, value: any) => (!isNaN(value)) ? newTotal + Number(value) : newTotal, 0);
    }, [purchasePrice, shippingFees, addFees]);

    useEffect(() => {
        const item = (selectedComicIds?.length === 1) ? getItem(selectedComicIds[0]) : null;
        form.setFieldValue('purchase_date', item ? item.purchase_date ? moment(item.purchase_date) : null : null);
        form.setFieldValue('purchase_price', item ? item.purchase_price : null);
        form.setFieldValue('purchase_shipping_fees', item ? item.purchase_shipping_fees : null);
        form.setFieldValue('purchase_additional_expenses', item ? item.purchase_additional_expenses : null);
        form.setFieldValue('purchase_venue', item ? item.purchase_venue : null);
        form.setFieldValue('purchase_notes', item ? item.purchase_notes : null);
    }, [selectedComicIds]);

    return (
        <Modal title={null} open={isModalOpen} onCancel={onCancel} centered={true} destroyOnClose={true} footer={null} className="edit-purchase-price-modal">
            <Form
                layout="horizontal"
                form={form}
                initialValues={{
                    location: 'None',
                }}
                labelCol={{span: 8}}
                wrapperCol={{span: 16}}
                onFinish={async (values: any) => {
                    if(selectedComicIds){
                        let sanitisedData = {
                            issue_ids: selectedComicIds,
                        };

                        sanitisedData = {...sanitisedData, ...values};
                        await assignIssuesPurchasePrice(sanitisedData);
                        form.resetFields();
                    }
                }}
            >
                <div className="modal-title text-center">
                    <span>Assign Purchase Price</span>
                </div>
                <Divider />

                <Row className="modal-form">
                    <Col span={24}>
                        <Form.Item label="Purchase Date" name="purchase_date">
                            <DatePicker
                                className={'width-100'}
                                format={(value) => `${value.format("YYYY/MM/DD")}`}
                            />
                        </Form.Item>

                        <Form.Item label="--" className="hide-form-label">
                            <div>
                                Total Comic Cost:
                                <span className="font-bold mar-left-10">{formatMoney(totalComicCost, false, true)}</span>
                            </div>
                        </Form.Item>

                        <Form.Item label="Purchase Price" name="purchase_price">
                            <InputNumber min={0} addonBefore="$" precision={2} placeholder="Enter Purchase Price"/>
                        </Form.Item>

                        <Form.Item label="Shipping Fee" name={`purchase_shipping_fees`}>
                            <InputNumber min={0} addonBefore="$" precision={2} placeholder=""/>
                        </Form.Item>

                        <Form.Item label="Additional Fees" name={`purchase_additional_expenses`}>
                            <InputNumber min={0} addonBefore="$" precision={2} placeholder=""/>
                        </Form.Item>

                        <Form.Item label="Sales Venue" name={`purchase_venue`}>
                            <Select
                                showSearch
                                onSearch={(value) => {
                                    if (venuesQuery.data) {
                                        setVenues([{venue: value}, ...venuesQuery.data]);
                                    } else {
                                        setVenues([{venue: value}]);
                                    }
                                    if(value && value !== '') form.setFieldValue('purchase_venue', value);
                                }}
                            >
                                <Select.Option value="">None</Select.Option>
                                {venues.map((venue, i) => (
                                    <Select.Option key={i}
                                                   value={venue.venue.toLowerCase()}>{formatTitleCase(venue.venue)}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item label="Purchase Notes" name="purchase_notes">
                            <TextArea rows={4} placeholder="" maxLength={1000} showCount/>
                        </Form.Item>

                    </Col>
                </Row>

                <Divider />
                <div className="text-center">
                    <Button type="primary"  shape="round"  size="large" htmlType="submit">
                        SUBMIT
                    </Button><br/>
                    <Button type="link" onClick={onCancel} >CANCEL</Button>
                </div>
            </Form>
        </Modal>
    );
};

export default AssignPurchasePriceModal;